// src/features/executive/insight/widgets/PatientCountryDonut.tsx
import { Box, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useMemo, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import { useTranslation } from 'react-i18next'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatVariance } from 'utils/executive.utils'
import { useCountry } from '../hooks/use-country'
import RevenueModeSelect from './RevenueModeSelect'
import type { RevenueMode } from 'api/executive/executive.types'
import { shouldUseBar } from 'features/executive/shared/utils/chart-mode'
import ProportionBar from './ProportionBar'

ChartJS.register(ArcElement, ChartTooltip, Legend)

// Distinct palette tuned for ≤9 segments (8 top + 1 rollup). Picked so adjacent
// slices in the donut don't blend (alternating warm/cool hues).
const PALETTE = ['#2a8d9c', '#e67e22', '#4caf50', '#9b59b6', '#f1c40f', '#3498db', '#e74c3c', '#1abc9c']
const ROLLUP_COLOR = '#90a4ae'

// Data-quality nudge threshold — > 10% of patients with no countryId on file.
const UNKNOWN_WARNING_PCT = 10
// Foreign-cohort callout threshold for dental-tourism / expat market value.
const FOREIGN_INSIGHT_PCT = 10

const fmt = (n: number) => new Intl.NumberFormat('th-TH').format(Math.round(n || 0))
const fmtBaht = (n: number) => `฿${fmt(n)}`

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

// Detect the home-country segment (Thailand for TDH). Accept ISO2/ISO3 or
// Thai-name fallback so the chip works across countryShort encodings.
function isThaiSegment(s: { shortCode: string | null; label: string }): boolean {
  if (s.shortCode === 'TH' || s.shortCode === 'THA') return true
  return (s.label || '').includes('ไทย')
}

export default function PatientCountryDonut() {
  const { t } = useTranslation()
  // Each monetary insight card owns its own revenue-mode state (per spec —
  // there's no dashboard-wide setting). Default 'fee' (ยอดค่ารักษา).
  const [revenueMode, setRevenueMode] = useState<RevenueMode>('fee')
  const { data, loading, error, refetch } = useCountry(revenueMode)

  const segments = data?.segments ?? []
  const useBar = shouldUseBar(segments.length)
  const maxPct = segments.length ? Math.max(...segments.map((s) => s.percent), 1) : 1

  const { chartData, colorByKey } = useMemo(() => {
    const map: Record<string, string> = {}
    segments.forEach((s, idx) => {
      map[s.key] = s.key === 'other_rollup' ? ROLLUP_COLOR : PALETTE[idx % PALETTE.length]
    })
    return {
      colorByKey: map,
      chartData: segments.length === 0 ? null : {
        labels: segments.map((s) => s.label),
        datasets: [
          {
            data: segments.map((s) => s.count),
            backgroundColor: segments.map((s) => map[s.key]),
            borderWidth: 0,
          },
        ],
      },
    }
  }, [segments])

  const total = data?.total ?? 0
  const totalRevenue = data?.totalRevenue ?? 0
  const previousTotal = data?.previousTotal ?? null
  const previousTotalRevenue = data?.previousTotalRevenue ?? null
  const unknownCount = data?.unknownCount ?? 0
  const isEmpty = !data || total === 0 || !chartData

  const totalVariance = varianceOf(total, previousTotal)
  const revenueVariance = varianceOf(totalRevenue, previousTotalRevenue)

  const unknownPct = total > 0 ? (unknownCount / total) * 100 : 0
  const showUnknownWarning = unknownPct > UNKNOWN_WARNING_PCT

  const foreignSegments = segments.filter((s) => !isThaiSegment(s) && s.key !== 'unknown')
  const foreignCount = foreignSegments.reduce((sum, s) => sum + s.count, 0)
  const foreignRevenue = foreignSegments.reduce((sum, s) => sum + s.revenue, 0)
  const foreignPct = total > 0 ? (foreignCount / total) * 100 : 0
  const foreignAvg = foreignCount > 0 ? Math.round(foreignRevenue / foreignCount) : 0
  const overallAvg = total > 0 ? Math.round(totalRevenue / total) : 0
  const foreignVsAvg = overallAvg > 0 ? ((foreignAvg - overallAvg) / overallAvg) * 100 : 0
  const showForeignInsight = foreignPct >= FOREIGN_INSIGHT_PCT

  const notice = (showUnknownWarning || showForeignInsight) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {showUnknownWarning && (
        <Chip
          icon={<WarningAmberOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.COUNTRY.UNSPECIFIED_WARNING',
                'ไม่ระบุประเทศ {{pct}}% — แนะนำให้แอดมินตรวจสอบข้อมูล "ประเทศ" ของคนไข้',
                { pct: unknownPct.toFixed(0) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {showForeignInsight && (
        <Chip
          icon={<PublicOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.COUNTRY.FOREIGN_INSIGHT',
                'ลูกค้าต่างชาติ {{pct}}% ({{count}} คน) — รายได้/คน ฿{{avg}} ({{delta}} เทียบเฉลี่ย)',
                {
                  pct: foreignPct.toFixed(1),
                  count: fmt(foreignCount),
                  avg: fmt(foreignAvg),
                  delta: `${foreignVsAvg >= 0 ? '+' : ''}${foreignVsAvg.toFixed(0)}%`,
                } as any
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  const subtitle = t(
    'EXECUTIVE.INSIGHT.COUNTRY.SUBTITLE',
    'แยกตามประเทศตามที่อยู่บัตรประชาชน — Top 8 + อื่นๆ รวม'
  )

  return (
    <WidgetCard
      title={t('EXECUTIVE.INSIGHT.COUNTRY.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.COUNTRY.SOURCE_TOOLTIP',
        'อ้างอิงจาก "ที่อยู่ตามบัตรประชาชน" ของคนไข้ ไม่ใช่สัญชาติ'
      )}
      subtitle={subtitle}
      headerAction={<RevenueModeSelect value={revenueMode} onChange={setRevenueMode} />}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.COUNTRY.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      onRetry={refetch}
      skeletonHeight={420}
    >
      {/* Summary strip: total patients + total revenue across all countries, with variance */}
      <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('EXECUTIVE.INSIGHT.TOTAL_PATIENTS', 'คนไข้ทั้งหมด')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {fmt(total)}
            </Typography>
            {totalVariance != null && (
              <Tooltip
                title={t(
                  'EXECUTIVE.INSIGHT.PREV_PERIOD_TOOLTIP',
                  'ช่วงก่อนหน้า: {{prev}}',
                  { prev: fmt(previousTotal ?? 0) }
                )}
                arrow
                placement="top"
              >
                <Typography
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                  color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                >
                  {totalVariance >= 0
                    ? <TrendingUpIcon sx={{ fontSize: 14, mr: 0.25 }} />
                    : <TrendingDownIcon sx={{ fontSize: 14, mr: 0.25 }} />}
                  {formatVariance(totalVariance)}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('EXECUTIVE.INSIGHT.COUNTRY.REVENUE', 'รายได้รวม')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {fmtBaht(totalRevenue)}
            </Typography>
            {revenueVariance != null && (
              <Tooltip
                title={t(
                  'EXECUTIVE.INSIGHT.PREV_PERIOD_TOOLTIP_BAHT',
                  'ช่วงก่อนหน้า: ฿{{prev}}',
                  { prev: fmt(previousTotalRevenue ?? 0) }
                )}
                arrow
                placement="top"
              >
                <Typography
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                  color={revenueVariance >= 0 ? 'success.main' : 'error.main'}
                >
                  {revenueVariance >= 0
                    ? <TrendingUpIcon sx={{ fontSize: 14, mr: 0.25 }} />
                    : <TrendingDownIcon sx={{ fontSize: 14, mr: 0.25 }} />}
                  {formatVariance(revenueVariance)}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Stack>

      {/* Donut for visual proportions */}
      {!useBar && (
        <Box sx={{ height: 160, position: 'relative', mb: 1.5 }}>
          {chartData && (
            <Doughnut
              data={chartData as any}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const value = Number(ctx.parsed)
                        const pct = total > 0 ? (value / total) * 100 : 0
                        return `${ctx.label}: ${fmt(value)} (${pct.toFixed(1)}%)`
                      },
                    },
                  },
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Rich legend: country name (+ ISO code) | count/% | variance | revenue/avg */}
      <Stack spacing={0.75}>
        {segments.map((s) => {
          const isRollup = s.key === 'other_rollup'
          const tooltipText = isRollup && s.restCountries && s.restCountries.length > 0
            ? `${s.label}\n• ${s.restCountries.join('\n• ')}`
            : s.label
          const variance = varianceOf(s.count, s.previousCount)
          return (
            <Box key={s.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10, height: 10, borderRadius: '50%',
                    backgroundColor: colorByKey[s.key] ?? '#95a5a6',
                    flexShrink: 0,
                  }}
                />
                <Tooltip
                  title={tooltipText}
                  placement="top-start"
                  enterDelay={400}
                  componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: isRollup ? 400 : 500,
                      fontStyle: isRollup ? 'italic' : 'normal',
                      color: isRollup ? 'text.secondary' : 'text.primary',
                      cursor: isRollup ? 'help' : 'default',
                    }}
                  >
                    {s.label}
                    {s.shortCode && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: 'text.disabled', ml: 0.5 }}
                      >
                        ({s.shortCode})
                      </Typography>
                    )}
                  </Typography>
                </Tooltip>
                <Typography variant="body2" color="text.primary">
                  {fmt(s.count)} ({s.percent.toFixed(1)}%)
                </Typography>
                {variance != null && (
                  <Typography
                    variant="caption"
                    color={variance >= 0 ? 'success.main' : 'error.main'}
                    sx={{ minWidth: 56, textAlign: 'right' }}
                  >
                    {formatVariance(variance)}
                  </Typography>
                )}
              </Box>
              {useBar && (
                <Box sx={{ mt: 0.5, mb: 0.25 }}>
                  <ProportionBar percent={s.percent} maxPercent={maxPct} color={colorByKey[s.key] ?? '#95a5a6'} />
                </Box>
              )}
              {/* Bottom hint: revenue + avg. Hidden when 0 revenue. */}
              {s.revenue > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2.25, mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {fmtBaht(s.revenue)} · {s.revenuePercent.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('EXECUTIVE.INSIGHT.COUNTRY.AVG_PER_PATIENT', 'เฉลี่ย/คน')}: {fmtBaht(s.avgRevenuePerPatient)}
                  </Typography>
                </Box>
              )}
            </Box>
          )
        })}
      </Stack>
    </WidgetCard>
  )
}
